import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../../config/api';
import { buildDuplicateGroups, customerFullName } from '../../utils/duplicateDetection';
import './styles/DuplicateCustomersReport.css';

const formatPhone = (phone) => {
  if (!phone) return 'N/A';
  const c = phone.replace(/\D/g, '');
  if (c.length === 10) return `(${c.slice(0, 3)}) ${c.slice(3, 6)}-${c.slice(6)}`;
  return phone;
};

const DuplicateCustomersReport = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterTerm, setFilterTerm] = useState('');
  const [busyId, setBusyId] = useState(null); // customer ID currently being deleted or moved
  const selectRefs = useRef({}); // memberId -> <select> DOM node, for reading the move target on click

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API_URL}/api/customers/reports/duplicates`);
      setCustomers(data || []);
    } catch (err) {
      console.error('Failed to load duplicates report:', err);
      setError('Failed to load customer data.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const groups = useMemo(() => buildDuplicateGroups(customers), [customers]);

  const filteredGroups = useMemo(() => {
    const term = filterTerm.trim().toLowerCase();
    if (!term) return groups;
    return groups.filter((g) =>
      g.some((c) =>
        customerFullName(c).toLowerCase().includes(term) ||
        (c.CustomerPhone || '').includes(term) ||
        (c.CustomerEmail || '').toLowerCase().includes(term)
      )
    );
  }, [groups, filterTerm]);

  const totalDuplicateCustomers = useMemo(() => groups.reduce((sum, g) => sum + g.length, 0), [groups]);

  const handleDelete = async (customer) => {
    if (busyId) return;
    if (customer.orderCount > 0 || customer.appointmentCount > 0) {
      toast.error("This account isn't empty — move its orders/appointments first.");
      return;
    }
    if (!window.confirm(`Delete ${customerFullName(customer)} (ID: ${customer.ID})? This cannot be undone.`)) return;

    setBusyId(customer.ID);
    try {
      await axios.delete(`${API_URL}/api/customers/${customer.ID}`);
      setCustomers((prev) => prev.filter((c) => c.ID !== customer.ID));
      toast.success(`${customerFullName(customer)} deleted.`);
    } catch (err) {
      if (err.response?.status === 409) {
        const { orders = 0, appointments = 0 } = err.response.data || {};
        const parts = [];
        if (orders) parts.push(`${orders} order${orders !== 1 ? 's' : ''}`);
        if (appointments) parts.push(`${appointments} appointment${appointments !== 1 ? 's' : ''}`);
        toast.error(`Can't delete — account isn't empty (${parts.join(', ')}).`);
      } else {
        toast.error(err.response?.data?.error || 'Failed to delete customer.');
      }
    } finally {
      setBusyId(null);
    }
  };

  const handleMove = async (fromCustomer) => {
    if (busyId) return;
    const select = selectRefs.current[fromCustomer.ID];
    const toId = select?.value;
    if (!toId) {
      toast.warn('Pick a destination customer first.');
      return;
    }
    const toName = select.options[select.selectedIndex]?.text || `customer ${toId}`;
    if (!window.confirm(`Move all orders & appointments from ${customerFullName(fromCustomer)} to ${toName}?`)) return;

    setBusyId(fromCustomer.ID);
    try {
      const { data } = await axios.put(`${API_URL}/api/customers/${fromCustomer.ID}/move-orders`, {
        toCustomerId: toId,
      });
      setCustomers((prev) => prev.map((c) => {
        if (c.ID === fromCustomer.ID) return { ...c, orderCount: 0, appointmentCount: 0 };
        if (String(c.ID) === String(toId)) {
          return {
            ...c,
            orderCount: c.orderCount + (data.ordersMoved || 0),
            appointmentCount: c.appointmentCount + (data.appointmentsMoved || 0),
          };
        }
        return c;
      }));
      toast.success(`Moved ${data.ordersMoved} order(s) and ${data.appointmentsMoved} appointment(s) to ${toName}.`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to move orders.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="dup-report-container">
      <div className="page-header">
        <div className="header-content">
          <div className="header-text">
            <h1 className="page-title"><i className="fas fa-clone" /> Duplicate Customers</h1>
            <p className="page-subtitle">Customers grouped by matching name or phone number — move their history, then delete the empty duplicate.</p>
          </div>
        </div>
      </div>

      <div className="dup-report-body">
        <div className="dup-report-toolbar">
          <button className="back-btn" onClick={() => navigate('/customers')}>
            <i className="fas fa-arrow-left" /> Back to Directory
          </button>
          <div className="dup-report-search">
            <i className="fas fa-search" />
            <input
              type="text"
              placeholder="Filter duplicate groups by name, phone, or email..."
              value={filterTerm}
              onChange={(e) => setFilterTerm(e.target.value)}
            />
          </div>
          <button className="btn-refresh" onClick={load} disabled={isLoading} title="Reload">
            <i className={`fas fa-sync-alt ${isLoading ? 'fa-spin' : ''}`} /> Refresh
          </button>
        </div>

        {isLoading && (
          <div className="dup-report-loading">
            <div className="loading-spinner" /> Scanning {customers.length > 0 ? customers.length : ''} customers for duplicates...
          </div>
        )}

        {!isLoading && error && (
          <div className="search-message error"><i className="fas fa-exclamation-triangle" /><span>{error}</span></div>
        )}

        {!isLoading && !error && (
          <>
            <div className="dup-report-summary">
              <i className="fas fa-exclamation-triangle" />
              <span><strong>{groups.length}</strong> duplicate group{groups.length !== 1 ? 's' : ''} found, covering <strong>{totalDuplicateCustomers}</strong> customer records.</span>
            </div>

            {filteredGroups.length === 0 && (
              <div className="no-results">
                <div className="no-results-icon"><i className="fas fa-check-circle" /></div>
                <h2>{groups.length === 0 ? 'No duplicates found' : 'No groups match your filter'}</h2>
              </div>
            )}

            <div className="dup-groups">
              {filteredGroups.map((group, gIdx) => (
                <div className="dup-group-card" key={gIdx}>
                  <div className="dup-group-header">
                    <i className="fas fa-users" />
                    <span>{group.length} matching records</span>
                  </div>
                  <div className="dup-group-members">
                    {group.map((member) => {
                      const otherMembers = group.filter((m) => m.ID !== member.ID);
                      const isEmpty = member.orderCount === 0 && member.appointmentCount === 0;
                      const isBusy = busyId === member.ID;
                      return (
                        <div className="dup-member-row" key={member.ID}>
                          <div className="dup-member-info">
                            <div className="dup-member-name">
                              {customerFullName(member) || 'Unknown'}
                              <span className="dup-member-id">ID: {member.ID}</span>
                            </div>
                            <div className="dup-member-contact">
                              <span><i className="fas fa-phone" /> {formatPhone(member.CustomerPhone)}</span>
                              {member.CustomerEmail && <span><i className="fas fa-envelope" /> {member.CustomerEmail}</span>}
                              {(member.CustomerCity || member.CustomerState) && (
                                <span><i className="fas fa-map-marker-alt" /> {[member.CustomerCity, member.CustomerState].filter(Boolean).join(', ')}</span>
                              )}
                            </div>
                            <div className="dup-member-counts">
                              <span className={`count-badge ${member.orderCount > 0 ? 'has-data' : 'is-empty'}`}>
                                <i className="fas fa-receipt" /> {member.orderCount} order{member.orderCount !== 1 ? 's' : ''}
                              </span>
                              <span className={`count-badge ${member.appointmentCount > 0 ? 'has-data' : 'is-empty'}`}>
                                <i className="fas fa-calendar" /> {member.appointmentCount} appointment{member.appointmentCount !== 1 ? 's' : ''}
                              </span>
                              {isEmpty && <span className="count-badge empty-flag"><i className="fas fa-check" /> Empty — deletable</span>}
                            </div>
                          </div>

                          <div className="dup-member-actions">
                            <button className="dup-action-btn" onClick={() => navigate(`/customers/${member.ID}`)} title="View profile">
                              <i className="fas fa-eye" /> View
                            </button>

                            {!isEmpty && otherMembers.length > 0 && (
                              <div className="dup-move-control">
                                <select ref={(el) => { selectRefs.current[member.ID] = el; }} defaultValue={otherMembers.length === 1 ? otherMembers[0].ID : ''}>
                                  {otherMembers.length > 1 && <option value="">Move to...</option>}
                                  {otherMembers.map((o) => (
                                    <option key={o.ID} value={o.ID}>{customerFullName(o)} (ID: {o.ID})</option>
                                  ))}
                                </select>
                                <button className="dup-action-btn move" onClick={() => handleMove(member)} disabled={isBusy}>
                                  {isBusy ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-arrow-right" />} Move here
                                </button>
                              </div>
                            )}

                            <button
                              className="dup-action-btn delete"
                              onClick={() => handleDelete(member)}
                              disabled={!isEmpty || isBusy}
                              title={isEmpty ? 'Delete this customer' : 'Move orders/appointments out first'}
                            >
                              {isBusy ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-trash-alt" />} Delete
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DuplicateCustomersReport;
